import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTooltipInPortal } from '@visx/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { oldTheme } from '../shared/old-styles';
import { theme } from '../shared/theme';
import { PlotTooltip, usePlotTooltip } from '../tooltips/plot-tooltip';
import { InfoIconProps } from './info-icons-general';

// Simple data type for our tooltip
interface TooltipData {
  showTooltip: boolean;
}

export interface MetricInfoIconProps extends InfoIconProps {
  modalContent: React.ReactNode;
}

export function MetricInfoIcon(props: MetricInfoIconProps) {
  const { style, isForceOpen, modalContent } = props;

  const [isModalOpen, setIsModalOpen] = useState(isForceOpen ?? false);
  // Control whether clicking outside closes the modal
  const [allowOutsideClick, setAllowOutsideClick] = useState(false);

  // Set up tooltip
  const tooltipStuff = useTooltipInPortal({
    scroll: false,
    detectBounds: true,
  });

  // Use a custom delay of 0 for immediate tooltip display
  const { tooltipData, tooltipProps, handleMouseMove } = usePlotTooltip<TooltipData>(tooltipStuff);

  // Reference to track the timeout for auto-opening the modal
  const openTimeoutRef = useRef<number | null>(null);
  // Reference to track the timeout for enabling outside clicks
  const protectionTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    // Clean up timeout on unmount
    return () => {
      if (openTimeoutRef.current) {
        window.clearTimeout(openTimeoutRef.current);
      }
      if (protectionTimeoutRef.current) {
        window.clearTimeout(protectionTimeoutRef.current);
      }
    };
  }, []);

  // Update modal open state based on isForceOpen prop changes
  useEffect(() => {
    if (isForceOpen !== undefined) {
      setIsModalOpen(isForceOpen);
      setAllowOutsideClick(true);
    }
  }, [isForceOpen]);

  // Open from hover (with protection)
  const handleHoverOpen = () => {
    // Initially don't allow outside clicks for hover-opened modals
    setAllowOutsideClick(false);
    setIsModalOpen(true);

    // Enable outside clicks after 1 second
    protectionTimeoutRef.current = window.setTimeout(() => {
      setAllowOutsideClick(true);
    }, 1000);

    // Notify about interaction via hover
    props.onInteraction?.();
  };

  // Open from click (without protection)
  const handleClickOpen = () => {
    // Always allow outside clicks for click-opened modals
    setAllowOutsideClick(true);
    setIsModalOpen(true);

    // Notify about interaction via click
    props.onInteraction?.();
  };

  const handleClose = () => {
    setIsModalOpen(false);
    // Reset state when closing
    setAllowOutsideClick(false);

    // Clear any pending timeout
    if (protectionTimeoutRef.current) {
      window.clearTimeout(protectionTimeoutRef.current);
      protectionTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<SVGSVGElement>) => {
    // Show tooltip immediately
    handleMouseMove(e, { showTooltip: true }, true);

    // Set up delayed modal opening
    openTimeoutRef.current = window.setTimeout(() => {
      handleHoverOpen();
    }, 1000); // 1 second delay
  };

  const handleMouseLeave = (e: React.MouseEvent<SVGSVGElement>) => {
    // Hide tooltip
    handleMouseMove(e, undefined);

    // Clear timeout to prevent modal from opening
    if (openTimeoutRef.current) {
      window.clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
  };

  return (
    <>
      <ClickableIcon
        icon={faQuestionCircle as IconProp}
        size="lg"
        onClick={handleClickOpen}
        style={{
          ...style,
          padding: 0,
          paddingBottom: 3,
          paddingRight: theme.spacing[2],
          color: isForceOpen ? 'black' : undefined,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={(e: React.MouseEvent<SVGSVGElement>) => {
          // Update tooltip position
          handleMouseMove(e, { showTooltip: true });
        }}
        onMouseLeave={handleMouseLeave}
      />

      {/* Render the tooltip if tooltipData exists */}
      {tooltipData && (
        <PlotTooltip {...tooltipProps} style={{ pointerEvents: 'none' }}>
          <div style={{ padding: theme.spacing[2], maxWidth: '250px' }}>
            <i>Hover for metric info!</i>
          </div>
        </PlotTooltip>
      )}

      <Modal
        size={'lg'}
        isOpen={isModalOpen}
        onClose={handleClose}
        isCentered
        closeOnOverlayClick={allowOutsideClick} // Only restrict overlay clicks in hover-opened modals
        closeOnEsc={true} // Always allow ESC key to close
      >
        <ModalOverlay />
        <ModalContent width="auto" maxWidth="600px" minWidth="400px">
          <ModalCloseButton />
          <ModalBody>{modalContent}</ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

// Styled component for the clickable icon
const ClickableIcon = styled(FontAwesomeIcon)`
  color: ${oldTheme.slightGray};
  cursor: pointer;
  &:hover {
    color: black;
  }
`;
